import { useEffect } from 'react';

interface UseHashScrollOptions {
    delay?: number;
    behavior?: ScrollBehavior;
    block?: ScrollLogicalPosition;
    clearHash?: boolean;
}

/**
 * Scrolls the element matching the URL hash into view once on mount, then clears the hash so a
 * refresh doesn't re-scroll. Content above the target must not shift after mount (reserve space
 * for late-loading content instead of re-scrolling — re-scrolls fight the user's own scrolling).
 */
export const useHashScroll = ({
    delay = 0,
    behavior = 'smooth',
    block = 'center',
    clearHash = true,
}: UseHashScrollOptions = {}) => {
    useEffect(() => {
        const hash = window.location.hash;
        if (!hash) {
            return undefined;
        }
        const elementId = hash.substring(1);

        const timer = window.setTimeout(() => {
            const element = document.getElementById(elementId);
            if (!element) {
                // Keep the hash so a later mount (or refresh) can still scroll to the target.
                return;
            }
            element.scrollIntoView({ behavior, block });
            if (clearHash) {
                window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
        }, delay);

        return () => window.clearTimeout(timer);
        // The hash is read once on mount by design.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
